// Часть разбиения MapView.vue (фаза 2) - перетаскиваемый курсор маршрута,
// синхронизированный с графиком высот (проп cursorDistanceKm в MapView.vue),
// плюс временная тултип-подсказка над ним.
//
// getRoutePoints/getCursorCumulativeKm/getRouteProfilePoints - геттеры, а не
// захваченные значения: эти три величины принадлежат useRouteDrawing.ts и
// переприсваиваются им на каждой загрузке маршрута (drawRoute/clearCurrentRoute),
// уже ПОСЛЕ того, как этот composable создан. См. тот же принцип у
// MapCtx.getMap в map/mapContext.ts.
import * as maplibregl from "maplibre-gl";
import type { Marker, Popup } from "maplibre-gl";
import type { MapCtx } from "../map/mapContext";
import { markerElement } from "../map/mapUiHelpers";
import { elevationAndGradeAtKm, nearestPointOnRoute, pointAtDistance } from "../utils/routeCursor";
import type { ElevationProfilePoint } from "../utils/routeCursor";
import type { PointLLE } from "../utils/routeLineStyle";

// Замена zIndexOffset из Leaflet - курсор маршрута должен лежать над пинами
// POI/конечных точек (см. тот же приём у USER_MARKER_Z в useGpsTracking.ts).
const CURSOR_MARKER_Z = 1500;

const CURSOR_TOOLTIP_HIDE_DELAY_MS = 2500; // как дефолтная длительность в useToast.ts

// Насколько близко (в экранных px) клик должен попасть по линии маршрута,
// чтобы поставить курсор - в пикселях, а не в метрах, чтобы «ощущение» было
// одинаковым на любом зуме (40м - это ~6px на z15, но ~46px на z12).
const CURSOR_CLICK_TOLERANCE_PX = 28;

export interface UseRouteCursorOptions {
  getRoutePoints: () => PointLLE[];
  getCursorCumulativeKm: () => number[];
  getRouteProfilePoints: () => ElevationProfilePoint[];
  emitCursorUpdate: (km: number | null) => void;
  emitMapTap: () => void;
}

export function useRouteCursor(ctx: MapCtx, options: UseRouteCursorOptions) {
  // Маленькая перетаскиваемая точка курсора маршрута - специально не похожа
  // на синюю GPS-точку «вы здесь» (без пульса, заливка primary-green), чтобы
  // их нельзя было спутать на экране одновременно.
  const createRouteCursorElement = () => {
    const el = markerElement("route-cursor-marker-icon", '<div class="route-cursor-marker"></div>');
    el.style.zIndex = String(CURSOR_MARKER_Z);
    return el;
  };

  let cursorMarker: Marker | null = null;
  // true, пока маркер перетаскивается - тогда watcher пропа cursorDistanceKm не
  // переставляет курсор, иначе он спорил бы сам с собой через цепочку emit->App.vue->prop.
  let cursorDragging = false;

  // Отдельный попап под своим управлением, а не привязанный к маркеру курсора:
  // привязанный попап переключается по клику на маркер, что конфликтует со
  // своей логикой show-during-drag / hide-after-delay.
  let cursorTooltip: Popup | null = null;
  let cursorTooltipHideTimer: number | null = null;

  const clearCursorTooltipHideTimer = () => {
    if (cursorTooltipHideTimer !== null) {
      window.clearTimeout(cursorTooltipHideTimer);
      cursorTooltipHideTimer = null;
    }
  };

  const hideCursorTooltip = () => {
    clearCursorTooltipHideTimer();
    cursorTooltip?.remove();
    cursorTooltip = null;
  };

  const scheduleCursorTooltipHide = () => {
    clearCursorTooltipHideTimer();
    cursorTooltipHideTimer = window.setTimeout(() => {
      cursorTooltipHideTimer = null;
      hideCursorTooltip();
    }, CURSOR_TOOLTIP_HIDE_DELAY_MS);
  };

  // Однострочная подсказка "N.N км · M м · ±G%" над маркером во время
  // перетаскивания - временная, не постоянная подпись. Плавающий бейдж
  // графика высот (profileCursor в RouteDetailsPanel.vue) специально другой,
  // многострочный формат - форматы больше не совпадают.
  const formatCursorTooltip = (km: number): string | null => {
    const at = elevationAndGradeAtKm(options.getRouteProfilePoints(), km);
    if (!at) return null;
    const gradeText = at.gradePct != null ? ` · ${at.gradePct > 0 ? "+" : ""}${at.gradePct.toFixed(1)}%` : "";
    return `${km.toFixed(1)} ${ctx.t("units.km")} · ${Math.round(at.elevationM)} ${ctx.t("units.m")}${gradeText}`;
  };

  const showCursorTooltip = (lng: number, lat: number, km: number) => {
    const map = ctx.getMap();
    if (!map) return;
    const label = formatCursorTooltip(km);
    if (!label) return;
    clearCursorTooltipHideTimer();
    if (cursorTooltip) {
      cursorTooltip.setLngLat([lng, lat]).setText(label);
      return;
    }
    cursorTooltip = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      closeOnMove: false,
      anchor: "bottom",
      offset: [0, -14],
      className: "route-cursor-tooltip",
      focusAfterOpen: false,
    })
      .setLngLat([lng, lat])
      .setText(label)
      .addTo(map);
  };

  const removeCursorMarker = () => {
    hideCursorTooltip();
    cursorMarker?.remove();
    cursorMarker = null;
    cursorDragging = false;
  };

  const placeCursorMarker = (lat: number, lng: number) => {
    const map = ctx.getMap();
    if (!map) return;
    if (cursorMarker) {
      cursorMarker.setLngLat([lng, lat]);
      return;
    }
    const marker = new maplibregl.Marker({
      element: createRouteCursorElement(),
      draggable: true,
    }).setLngLat([lng, lat]);
    // Непрерывное «магнитное» прилипание: на каждом тике перетаскивания сырая
    // позиция маркера проецируется на ближайшую точку маршрута - маркер
    // визуально держится на линии, а не летает свободно.
    marker.on("dragstart", () => {
      cursorDragging = true;
    });
    marker.on("drag", () => {
      const routePoints = options.getRoutePoints();
      if (routePoints.length < 2) return;
      const raw = marker.getLngLat();
      const snapped = nearestPointOnRoute(routePoints, raw.lat, raw.lng, options.getCursorCumulativeKm());
      if (!snapped) return;
      marker.setLngLat([snapped.lng, snapped.lat]);
      showCursorTooltip(snapped.lng, snapped.lat, snapped.km);
      options.emitCursorUpdate(snapped.km);
    });
    marker.on("dragend", () => {
      cursorDragging = false;
      scheduleCursorTooltipHide();
    });
    marker.addTo(map);
    cursorMarker = marker;
  };

  // Реакция на изменение курсора извне (клик/драг на графике высот) -
  // переводит км в lnglat и двигает маркер.
  const syncCursorFromProp = (km: number | null | undefined) => {
    if (cursorDragging) return;
    const routePoints = options.getRoutePoints();
    if (typeof km !== "number" || routePoints.length < 2) {
      removeCursorMarker();
      return;
    }
    const pos = pointAtDistance(routePoints, km, options.getCursorCumulativeKm());
    if (pos) placeCursorMarker(pos.lat, pos.lng);
  };

  // Постановка курсора кликом: только при выбранном маршруте и только если
  // клик попал достаточно близко к линии на экране. В отличие от Leaflet,
  // маркеры и попапы MapLibre не глотают клики по карте (это DOM-соседи над
  // тем же canvas-контейнером), поэтому клики по ним нужно фильтровать явно.
  const handleMapClickForCursor = (e: maplibregl.MapMouseEvent) => {
    const map = ctx.getMap();
    if (!map) return;
    // Иначе клик рядом с линией в режиме редактирования одновременно ставил
    // бы курсор маршрута, пока пользователь пытается открыть редактор POI.
    if (ctx.editMode.value) return;
    // Маркеры и попапы лежат в том же canvas-контейнере, на который подписана
    // карта, поэтому их клики тоже всплывают сюда. У клика, реально попавшего
    // по карте, target - это сам canvas; всё остальное пришло от маркера,
    // попапа или контрола и не должно ставить курсор маршрута.
    const target = e.originalEvent?.target as HTMLElement | null;
    if (target && target !== map.getCanvas()) return;

    // Тап по линии выбранного маршрута - не «пустая карта» (карточку не сворачивает).
    const routePoints = options.getRoutePoints();
    const snapped = routePoints.length < 2 ? null : nearestPointOnRoute(routePoints, e.lngLat.lat, e.lngLat.lng, options.getCursorCumulativeKm());
    if (!snapped) {
      options.emitMapTap();
      return;
    }
    const clickPt = map.project(e.lngLat);
    const snapPt = map.project([snapped.lng, snapped.lat]);
    if (Math.hypot(clickPt.x - snapPt.x, clickPt.y - snapPt.y) > CURSOR_CLICK_TOLERANCE_PX) {
      options.emitMapTap();
      return;
    }
    placeCursorMarker(snapped.lat, snapped.lng);
    // Та же временная подсказка, что и при перетаскивании - обычный клик для
    // пользователя выглядит как драг нулевой длины.
    showCursorTooltip(snapped.lng, snapped.lat, snapped.km);
    scheduleCursorTooltipHide();
    options.emitCursorUpdate(snapped.km);
  };

  return {
    removeCursorMarker,
    placeCursorMarker,
    syncCursorFromProp,
    handleMapClickForCursor,
  };
}
